import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface Brand {
  id: string
  name: string
  country?: string
  description?: string
  website?: string
  logoUrl?: string
  foundedYear?: number
  createdAt: string
  updatedAt: string
}

export interface CigarLine {
  id: string
  brandId: string
  name: string
  description?: string
  strengthLevel?: string
  wrapperType?: string
  binderType?: string
  fillerType?: string
  createdAt: string
  updatedAt: string
  brand: Brand
}

export interface BrandApiResponse<T = unknown> {
  success: boolean
  message: string
  data: T
}

export const brandApi = createApi({
  reducerPath: 'brandApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/brands',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Brand', 'CigarLine'],
  endpoints: (builder) => ({
    // Get all brands
    getBrands: builder.query<Brand[], { search?: string }>({
      query: (params) => ({
        url: '',
        params,
      }),
      transformResponse: (response: BrandApiResponse<Brand[]>) => response.data,
      providesTags: ['Brand'],
    }),

    // Get brand by ID
    getBrand: builder.query<Brand, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: BrandApiResponse<Brand>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Brand', id }],
    }),

    // Get cigar lines for a brand
    getCigarLines: builder.query<CigarLine[], string>({
      query: (brandId) => `/${brandId}/cigar-lines`,
      transformResponse: (response: BrandApiResponse<CigarLine[]>) => response.data,
      providesTags: ['CigarLine'],
    }),

    // Get cigar line by ID
    getCigarLine: builder.query<CigarLine, string>({
      query: (id) => `/cigar-lines/${id}`,
      transformResponse: (response: BrandApiResponse<CigarLine>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'CigarLine', id }],
    }),
  }),
})

export const {
  useGetBrandsQuery,
  useGetBrandQuery,
  useGetCigarLinesQuery,
  useGetCigarLineQuery,
} = brandApi
